/**
 * Subscription Confirmation Page
 *
 * Displays subscription confirmation after successful payment and subscription creation.
 *
 * Route: /subscriptions/confirmed/:compositeId
 * Format: membership-123 or pass-456
 *
 * Data Flow:
 * - Parse composite ID from URL (membership-123 or pass-456)
 * - Fetch subscription data via API (handles page refresh)
 * - Normalize data for unified display
 * - Clear purchase context on mount
 *
 * Auth Requirements:
 * - Protected route (requires authentication)
 * - Authorization check: subscription.userId must match authenticated user
 *
 * @module pages/subscription
 */

import { useMemo } from "react";
import { useParams, useLocation } from "react-router";
import { useAuth } from "~/services/auth";
import { useClubQuery } from "~/services/internalApi/club/hooks/useClubQuery";
import { useProcessingFeeGate } from "~/services/internalApi/club";
import { formatClubDate, asUtcDate } from "@butter/library/datetime";
import { formatFeeRate } from "@butter/library/pricing";
import { parseConfirmationId } from "~/lib/utils/subscriptionIdUtils";
import { captureException } from "@butter/monitoring";
import { calcBillingDetails } from "@butter/ui/utils";
import { 
  isMembershipRecurringMetadata,
  isPassPurchaseMetadata,
} from "@lgc/database/types";
import {
  useUserPassQuery,
} from "~/services/internalApi/subscription/hooks/useSubscriptionConfirmationQueries";
import { useInvoiceQuery } from "~/services/internalApi/invoice";
import type {
  ConfirmationData,
  SubscriptionConfirmationState,
} from "~/services/internalApi/subscription/types/confirmationData";
import { useClearSubscriptionOnConfirmation } from "~/services/internalApi/subscription/utils/clearSubscriptionContext";
import { useActiveSubscription } from "~/services/internalApi/subscription";
import { useHashModal } from "~/hooks/useHashModal";
import {
  TicketCard,
  TicketDivider,
} from "@butter/ui/patterns";
import { CopyButton } from "@butter/ui/primitives";
import { SubscriptionPlanCard, type SubscriptionPlan } from "@butter/ui/domains/subscriptions";
import { CheckmarkIcon } from "@butter/ui/icons";
import { TenantLink } from "~/lib/navigation";
import { safeToNumericUserId } from "~/lib/utils/userIdUtils";

/**
 * Subscription Confirmation Page Component
 */
export function SubscriptionConfirmationPage() {
  const { compositeId } = useParams<{ compositeId: string }>();
  const location = useLocation();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  // Club data is guaranteed loaded by ClubGate; only this page's own data
  // (pass/membership) contributes to the loading state below.
  const { data: club } = useClubQuery();
  // The member's tenant-wide completed-demo stamp (demo bookings D38) — pairs with the
  // purchased plan's demoRequired for the "book your demo" CTA (demo bookings D40). Read
  // from the subscription payload rather than the purchase response so an
  // already-stamped member never sees the CTA, whatever they just bought.
  // Also the refresh path of a MEMBERSHIP receipt: the member's own card rides
  // in `holdings` (subscription unification D12.4), so the page needs no
  // per-row read — the one it used to make hit a route that was never mounted.
  const {
    demoCompleted,
    activeSubscription,
    holdings,
    isLoading: isLoadingActive,
    error: activeError,
  } = useActiveSubscription({
    userId: user?.id != null ? Number(user.id) : undefined,
  });
  // The active plan's flag (include=plan projection, D37) — the membership
  // confirmation wire is a slim legacy projection, so this is the reliable
  // template-flag source for the CTA below.
  const activePlanDemoRequired = activeSubscription?.plan?.demoRequired ?? false;

  // Parse composite ID from URL. `gift-<invoiceId>` is a receipt for an order
  // the buyer kept nothing from — it names an invoice, not a plan, so none of
  // the plan queries below fire for it.
  const parsed = compositeId ? parseConfirmationId(compositeId) : null;

  // Try to get subscription data from navigation state
  const stateData = location.state as SubscriptionConfirmationState | null;

  // Clear subscription purchase context on mount
  useClearSubscriptionOnConfirmation();

  // Opens the global SignInModal rendered by MainLayout (same hash key).
  const signInModal = useHashModal("dialog-sign-in");

  // Get user ID for membership query
  const userId = safeToNumericUserId(user?.id) || 0;

  // Priority 1: Use subscription from state (fast, no API call)
  // Priority 2: Fetch subscription by ID from API (page refresh scenario) — only once
  // authenticated, since these endpoints 401 for guests. React Query auto-enables and
  // refetches when auth flips true post-login.
  // Fetch pass data (fetches if composite ID is pass type)
  const {
    data: passData,
    isLoading: isLoadingPass,
    error: passError,
  } = useUserPassQuery(
    userId,
    parsed?.type === "pass" ? parsed.id : 0,
    isAuthenticated
  );

  const parsedType = parsed?.type;
  const parsedId = parsed?.id;
  // A membership receipt with no navigation state is built from the holdings
  // read, so that read's failure is this page's error. With state present the
  // read is only a fallback (below), and its failure must not hide a receipt the
  // state already carries — the error panel renders ahead of the receipt.
  const membershipFromHoldings = parsedType === "membership" && !stateData?.subscription;
  const membershipError = membershipFromHoldings ? activeError : null;
  const invoiceId = useMemo((): number | null => {
    // Both response payloads (`CreateUserMembershipResponse | CreateUserPassResponse`) declare
    // `invoice?: { id: number; … }`, so the id is typed — no cast needed.
    const id = stateData?.subscription?.invoice?.id
      ?? (parsedType === 'gift' ? parsedId : undefined);
    return id != null && id > 0 ? id : null;
  }, [stateData, parsedType, parsedId]);

  // Fetch the canonical InvoiceDTO to itemize the processing fee — but only when the CHARGING
  // (home) club passes fees, so we don't fire an authenticated request on the majority of
  // confirmations that never show the row. Gating on the charging club (not the visited club)
  // avoids hiding a real fee for a cross-club member. Requires auth (the endpoint 401s for guests).
  //
  // A GIFT receipt fetches regardless of that gate: it is keyed by the invoice
  // and owns no pass, so without this a refresh has nothing to rebuild from and
  // a paying buyer reads "Subscription Not Found".
  const { passProcessingFees } = useProcessingFeeGate();
  const { invoice: fetchedInvoice, error: invoiceError } = useInvoiceQuery({
    invoiceId,
    enabled: isAuthenticated && invoiceId !== null && (passProcessingFees || parsedType === 'gift'),
  });


  // Prepare confirmation data using discriminated unions
  const confirmationData: ConfirmationData | null = useMemo(() => {
    // Priority 1: Use navigation state data (from purchase flow redirect)
    if (stateData?.subscription && stateData?.type) {
      if (stateData.type === "gift") {
        // A purchase the buyer kept nothing from: no `userPass` at all, so the
        // receipt is built from the hoisted plan template plus the recipients
        // the buyer typed. Nothing here is owner-scoped — the buyer owns the
        // invoice, not any of the passes this order created.
        //
        // No cast: `CreateUserPassPayload` declares both optional fields, so
        // narrowing on them gives the same guards the cast used to need by hand.
        const payload = stateData.subscription;
        const pass = 'pass' in payload ? payload.pass : undefined;
        const gifted = ('giftedPasses' in payload ? payload.giftedPasses : undefined) ?? [];
        // Without the template there is nothing to name, and without recipients
        // nothing to report. Genuinely FALL THROUGH — an earlier version said so
        // and then `return null`ed, which skipped the invoice rebuild below. That
        // rebuild can now serve this case, so a malformed create response should
        // reach it rather than become "Subscription Not Found".
        if (pass && gifted.length > 0) {
          return {
            type: 'gift',
            pass,
            recipients: gifted.map((entry) => entry.recipient),
            pricing: stateData.pricing,
          };
        }
      } else if (stateData.type === "pass") {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const apiResponse = stateData.subscription as any;
        // stateData.subscription is already the unwrapped mutation payload ({ userPass, invoice }).
        const rawPass = apiResponse.userPass;

        // Defensive: a PURE GIFT sends `type: 'gift'` above and never lands
        // here, but `as any` means nothing would stop a malformed state from
        // dereferencing `undefined.pass` and blanking the page after a charge.
        if (!rawPass) {
          return null;
        }

        // Backend returns capital letter keys (Pass, User) in create response
        // but lowercase keys (pass, club) in fetch response
        // Normalize to lowercase for consistent handling
        const pass = rawPass.pass || rawPass.Pass;

        // Validate that critical pass data exists
        // If missing, fall back to API fetch instead of crashing later
        if (!pass) {
          // Pass data is incomplete, skip navigation state and trigger API fetch
          return null;
        }
        
        const userPass = {
          ...rawPass,
          pass,
          club: rawPass.club || pass.club, // Club is optional (handled with ?. in display)
        };
        
        return {
          type: 'pass',
          userPass,
          pricing: stateData.pricing,
        };
      }
      if (stateData.type === "membership") {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const apiResponse = stateData.subscription as any;
        // stateData.subscription is already the unwrapped mutation payload ({ userMembership, invoice }).
        const rawMembership = apiResponse.userMembership;

        // The API returns one shape: `userMembership.membership` (with its `club`).
        const membership = rawMembership.membership;

        // Validate that critical membership data exists
        // If missing, fall back to API fetch instead of crashing later
        if (!membership) {
          // Membership data is incomplete, skip navigation state and trigger API fetch
          return null;
        }

        const userMembership = {
          ...rawMembership,
          membership,
          club: membership.club, // Club is optional (handled with ?. in display)
        };
        
        return {
          type: 'membership',
          userMembership,
          pricing: stateData.pricing,
        };
      }
    }
    
    // Priority 2: Use fetched API data (from page refresh)
    if (parsed?.type === "pass" && passData?.success) {
      return {
        type: 'pass',
        userPass: passData.payload.userPass,
        pricing: undefined, // No pricing info on refresh
      };
    }
    if (parsed?.type === "membership" && holdings) {
      // The member's own card, by the id in the url. `plan` carries the name and
      // description (include=plan); the club is the page's own query.
      const card = holdings.memberships.find((c) => c.id === parsed.id);
      if (!card) return null;
      return {
        type: 'membership',
        userMembership: {
          id: card.id,
          userId: card.userId,
          startDate: card.startDate ?? card.createdAt,
          billingFrequency: card.billingFrequency ?? '',
          price: card.price ?? '0.00',
          ended: card.ended,
          membership: {
            id: card.membershipId,
            name: card.plan?.name ?? 'Membership',
            description: card.plan?.description ?? null,
          },
          club: { name: club?.name ?? '' },
        },
        pricing: undefined, // No pricing info on refresh
      };
    }
    // A GIFT receipt rebuilt from the invoice the buyer owns. Reduced on purpose:
    // the plan and the total survive, the RECIPIENTS do not — their names live
    // only in the create response, never on an invoice, and inventing them is
    // not an option. So a reopened gift receipt says the passes were sent
    // without naming who to.
    if (parsed?.type === "gift" && fetchedInvoice) {
      // A REVERSAL is a second invoice owned by the same buyer, whose lines copy
      // the original's `purchasableType` with a `Reversal — ` title and a
      // NEGATIVE amount. So its id is legitimately theirs and everything below
      // would happily render "Your gift is on its way" at $-45.00 — and a +1 from
      // the original's id often lands on one.
      if (fetchedInvoice.kind === 'reversal') return null;
      // `type === 'charge'`: a promo's discount line is also `purchasableType:
      // 'pass'` with a negative unitPrice. Charge lines are authored first today,
      // so `find` happens to skip it — the guard is what makes that intentional.
      const passLine = fetchedInvoice.lineItems.find(
        (line) => line.type === 'charge' && line.purchasableType === 'pass',
      );
      if (!passLine) return null;
      return {
        type: 'gift',
        // The line title is authored as `Pass: <name>` by the charge path.
        // `unitPrice`, NOT `amount`: the DTO defines `amount` as the EXTENDED
        // price (`quantity × unitPrice`). An order authored as one line of
        // quantity N — which the same builder emits on the quote path — would
        // otherwise show N passes' worth as the price of one.
        pass: {
          id: passLine.purchasableId ?? 0,
          name: passLine.title.replace(/^Pass:\s*/, ''),
          description: null,
          price: passLine.unitPrice.toFixed(2),
        },
        recipients: [],
        pricing: { subtotal: fetchedInvoice.totals.subtotal, tax: fetchedInvoice.totals.tax,
          total: fetchedInvoice.totals.total, taxRate: fetchedInvoice.totals.taxRate },
      };
    }
    
    return null;
  }, [stateData, parsed, passData, holdings, club?.name, fetchedInvoice]);

  // Total paid — SERVER figures only. The old Priority-3 fallback recomputed
  // base price × tax client-side; it ignored the setup fee and the processing
  // fee, so a hard refresh could show a receipt that disagreed with the amount
  // actually charged. Gone with the D12 quote work: the client renders money,
  // it never derives it. With neither navigation state nor invoice metadata
  // (hard refresh / shared link) the row says it could not load — honest, and
  // rarer than a wrong number. DELIBERATE TRADE, tracked in
  // apps/e2e/docs/known-issues.md (Group U): the follow-up is a purchasable
  // filter on GET /invoices/user/:userId (filter field + JSONB predicate +
  // query-param wiring — createInvoice already hoists metadata.purchasableId
  // for this lookup) or an invoice id on the pass/membership reads.
  const totalPaid = useMemo((): number | null => {
    // Priority 1: Use pre-calculated pricing from navigation state. `!= null`,
    // not truthiness — a $0 plan's total IS 0, and treating it as missing was
    // the Group-F bug ("Unable to load purchase price" on free confirmations).
    if (stateData?.pricing?.total != null) {
      return stateData.pricing.total;
    }

    // Priority 1b: a GIFT receipt reopened without navigation state. It is keyed
    // by the invoice and fetched it above, so the figure IS known here — the
    // Group-U "no client recompute" rule is about not DERIVING a total, and this
    // one is the server's own. Without this the page recovers the plan and the
    // recipients line and then claims it cannot find the price.
    if (fetchedInvoice && parsedType === 'gift') {
      return fetchedInvoice.totals.total;
    }

    // Priority 2: Extract from invoice metadata if available
    if (stateData?.subscription) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const invoice = (stateData.subscription as any)?.invoice;
      if (invoice?.metadata) {
        const metadata = invoice.metadata;
        // Check for membership or pass invoice metadata
        if (isMembershipRecurringMetadata(metadata) || isPassPurchaseMetadata(metadata)) {
          // Use originalAmount (total with tax) if available
          if (metadata.originalAmount !== undefined) {
            return metadata.originalAmount;
          }
          // Use finalAmount (after credits) if available
          if (metadata.finalAmount !== undefined) {
            return metadata.finalAmount;
          }
        }
      }
    }

    // No client recompute (see the memo doc above).
    return null;
  }, [stateData, fetchedInvoice, parsedType]);

  // The paid invoice's canonical id, when the create-mutation payload is present in
  // navigation state (`stateData.subscription.invoice.id`). On a hard refresh
  // (Priority-2 fetch path) there is no invoice id available, so the processing-fee
  // row simply does not render.
  // A GIFT receipt is keyed by the invoice, so the url carries it even when
  // navigation state does not — deps stay primitive because
  // `parsed` is rebuilt every render.
  // Determine loading state
  // A GIFT receipt with no navigation state has to wait for the invoice before it
  // can say anything — and until it does, BOTH plan queries are disabled, which
  // TanStack reports as `isLoading: false`. Without this the buyer who just paid
  // sees the full "Subscription Not Found" panel for the whole round-trip: the
  // exact panel the invoice rebuild exists to prevent.
  //
  // Deliberately narrow. Folding `isLoadingInvoice` into the general gate would
  // make an ordinary PASS receipt — which already has everything in navigation
  // state — sit on a spinner waiting for a query it only needs for the
  // processing-fee row. `!fetchedInvoice && !invoiceError` rather than
  // `isLoadingInvoice` alone, because the query is DISABLED (not loading) while
  // auth restores, and because a 403 must stop the wait instead of spinning forever.
  const giftRebuildPending =
    parsedType === 'gift' && !stateData?.subscription && !fetchedInvoice && !invoiceError;
  // A membership receipt waits on the holdings read whenever nothing else could
  // build it — no navigation state, OR state whose membership is missing and
  // fell through to the holdings fallback. Keyed on the memo's result rather
  // than on the presence of state so that fallback does not flash "Not Found"
  // for the round-trip (the read is disabled until auth restores).
  const isLoadingMembership =
    parsedType === "membership" && !confirmationData && isAuthenticated && isLoadingActive;
  const isLoading = authLoading || isLoadingPass || isLoadingMembership || giftRebuildPending;

  // Determine error state
  // A gift receipt's only read is the invoice, so its failure is this page's
  // error — otherwise a 403 or a 404 is indistinguishable from "no such receipt"
  // and the member is told it does not exist.
  const errorMessage =
    passError?.message || membershipError?.message ||
    (parsedType === 'gift' ? invoiceError?.message ?? null : null);

  // Check authorization (subscription must belong to current user)
  const isUnauthorized = useMemo(() => {
    if (!confirmationData || !user) return false;

    // A gift receipt owns no subscription to compare against — every pass in
    // that order belongs to a recipient by design. What the buyer owns is the
    // invoice, and it only ever reaches them through their own navigation
    // state, so there is nothing here to authorize. Comparing
    // anyway is precisely what made this page refuse the buyer their receipt.
    if (confirmationData.type === 'gift') return false;

    const subscriptionUserId = confirmationData.type === 'pass'
      ? confirmationData.userPass.userId
      : confirmationData.userMembership.userId;

    // Normalize user.id to number for comparison (user.id can be string or number)
    const authenticatedUserId = safeToNumericUserId(user.id);
    if (authenticatedUserId === null) {
      // Can't verify authorization without valid user ID - be safe and block access
      return true;
    }
    
    return subscriptionUserId !== authenticatedUserId;
  }, [confirmationData, user]);

  // Extract data for display based on subscription type
  const displayData = useMemo(() => {
    if (!confirmationData || !club?.timezone) return null;
    
    if (confirmationData.type === 'gift') {
      // No dates: the buyer holds nothing, and a recipient's validity window is
      // theirs. Only the plan bought, what it cost, and who it went to.
      const { pass, recipients } = confirmationData;
      return {
        id: pass.id,
        name: pass.name,
        description: pass.description ?? null,
        // The TEMPLATE's price, where the pass branch below uses what the member
        // was actually charged (`userPass.price`). They agree on every member
        // purchase — `userPasses.price` is snapshotted from the override or the
        // template, and the public path sets no override — and a gift has no
        // `userPass` to read the charged figure from. On a rebuild this is the
        // invoice line's own amount, which is the charged figure.
        price: pass.price ?? '0',
        subscriptionType: 'gift' as const,
        recipients,
        clubName: club.name || 'Unknown Club',
        // A gift is the recipients' plan, not the buyer's — no demo CTA (demo bookings D40).
        demoRequired: false,
      };
    }

    if (confirmationData.type === 'pass') {
      const { userPass } = confirmationData;
      return {
        id: userPass.id,
        name: userPass.pass?.name ?? '',
        description: userPass.pass?.description ?? null,
        price: userPass.price ?? userPass.pass?.price ?? '0', // What this member paid; template is the legacy-row fallback
        subscriptionType: 'pass' as const,
        startDate: userPass.purchaseDate,
        expirationDate: userPass.expirationDate,
        validityDays: userPass.pass?.validityDays ?? 0, // Pass template validity
        clubName: userPass.club?.name || 'Unknown Club',
        // Required-demo rule (demo bookings D37/D40): drives the "book your demo" CTA below.
        demoRequired: userPass.pass?.demoRequired ?? false,
      };
    } else {
      const { userMembership } = confirmationData;
      
      // Use billing details utility for consistent date calculations
      const billingDetails = calcBillingDetails(
        {
          subscriptionType: 'membership',
          billingFrequency: userMembership.billingFrequency,
          startDate: userMembership.startDate,
        },
        club.timezone
      );
      
      return {
        id: userMembership.id,
        name: userMembership.membership.name,
        description: userMembership.membership.description,
        price: userMembership.price.toString(),
        subscriptionType: 'membership' as const,
        startDate: userMembership.startDate,
        nextBillingDate: billingDetails.nextBillingDateObj?.toISOString() || '',
        billingFrequency: userMembership.billingFrequency,
        isActive: !userMembership.ended,
        clubName: userMembership.club.name,
        // The legacy list wire projects a slim membership with no template
        // flags — the CTA falls back to the active-subscription plan (a just
        // purchased membership IS the active one).
        demoRequired: false,
      };
    }
  }, [confirmationData, club]);

  // Render error states
  if (!compositeId || !parsed) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Invalid Subscription Link
          </h1>
          <p className="text-gray-600 mb-6">
            The subscription link you followed is invalid.
          </p>
          <TenantLink
            to="/subscriptions"
            className="inline-block px-6 py-2 bg-theme-600 text-white rounded-full font-medium hover:bg-theme-700"
          >
            View Subscriptions
          </TenantLink>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading subscription details...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated && !confirmationData) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        data-testid="subscription-confirmation-signin-required"
      >
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Sign in Required</h1>
          <p className="text-gray-600 mb-6">
            Please sign in to view your subscription details.
          </p>
          <button
            onClick={signInModal.open}
            className="px-6 py-2 bg-theme-600 text-white rounded-full font-medium text-interactive:bg-theme-700"
            aria-label="Sign in to view your subscription"
            data-testid="subscription-confirmation-signin-btn"
          >
            Sign in
          </button>
        </div>
      </div>
    );
  }

  if (isUnauthorized) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">
            Unauthorized
          </h1>
          <p className="text-gray-600 mb-6">
            This subscription belongs to a different account.
          </p>
          <TenantLink
            to="/subscriptions"
            className="inline-block px-6 py-2 bg-theme-600 text-white rounded-full font-medium hover:bg-theme-700"
          >
            View Subscriptions
          </TenantLink>
        </div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Error</h1>
          <p className="text-gray-600 mb-6">{errorMessage}</p>
          <TenantLink
            to="/subscriptions"
            className="inline-block px-6 py-2 bg-theme-600 text-white rounded-full font-medium hover:bg-theme-700"
          >
            View Subscriptions
          </TenantLink>
        </div>
      </div>
    );
  }

  if (!confirmationData) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Subscription Not Found
          </h1>
          <p className="text-gray-600 mb-6">
            We couldn't find this subscription. Please try again or contact the club.
          </p>
          <TenantLink
            to="/subscriptions"
            className="inline-block px-6 py-2 bg-theme-600 text-white rounded-full font-medium hover:bg-theme-700"
          >
            View Subscriptions
          </TenantLink>
        </div>
      </div>
    );
  }

  // At this point, confirmationData is guaranteed to exist, and displayData should exist
  if (!displayData) {
    // This should never happen now that isLoading includes club loading state
    // Log error for debugging if it does occur
    captureException(new Error('displayData is null despite confirmationData existing'), {
      level: 'error',
      tags: {
        component: 'subscription-confirmation',
        severity: 'critical',
      },
      extra: {
        hasConfirmationData: !!confirmationData,
        hasClub: !!club,
        hasClubTimezone: !!club?.timezone,
        message: 'displayData unexpectedly null - logic error in useMemo',
      },
    });
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Unable to Display Confirmation
          </h1>
          <p className="text-gray-600 mb-6">
            We encountered an unexpected error. Please try refreshing the page.
          </p>
          <TenantLink
            to="/subscriptions"
            className="inline-block px-6 py-2 bg-theme-600 text-white rounded-full font-medium hover:bg-theme-700"
          >
            View Subscriptions
          </TenantLink>
        </div>
      </div>
    );
  }

  // Validate club timezone is available for date formatting
  if (!club?.timezone) {
    captureException(new Error('Club timezone not available'), {
      level: 'error',
      tags: {
        component: 'subscription-confirmation',
        severity: 'high',
      },
      extra: {
        clubId: club?.id,
        message: 'Club timezone required for date formatting',
      },
    });
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Unable to Load Club Information
          </h1>
          <p className="text-gray-600 mb-6">
            We couldn't load the club information needed to display your confirmation. Please try again.
          </p>
          <TenantLink
            to="/subscriptions"
            className="inline-block px-6 py-2 bg-theme-600 text-white rounded-full font-medium hover:bg-theme-700"
          >
            View Subscriptions
          </TenantLink>
        </div>
      </div>
    );
  }

  // A gift receipt has no dates of the buyer's: they hold nothing, and each
  // recipient's validity window is theirs.
  const isGift = displayData.subscriptionType === 'gift';

  // Format start date for display using club's timezone
  const startDateFormatted = displayData.subscriptionType === 'gift'
    ? null
    : formatClubDate(
        asUtcDate(new Date(displayData.startDate)),
        club.timezone,
        "EEEE, d MMMM, yyyy"
      );

  // Format billing/expiration info using club's timezone
  const billingInfo = displayData.subscriptionType === 'gift'
    ? null
    : displayData.subscriptionType === 'pass'
      ? (displayData.expirationDate
          ? formatClubDate(
              asUtcDate(new Date(displayData.expirationDate)),
              club.timezone,
              "'Expires' MMM d, yyyy"
            )
          : 'No expiration')
      : displayData.billingFrequency.charAt(0).toUpperCase() + displayData.billingFrequency.slice(1);

  // Convert confirmation data to SubscriptionPlan format for card
  const subscriptionPlan: SubscriptionPlan = {
    id: displayData.id,
    name: displayData.name,
    description: displayData.description || "",
    price: `$${Number(displayData.price).toFixed(2)}`,
    // The card knows only plans; a gift IS a pass, just not the buyer's.
    subscriptionType: isGift ? 'pass' : displayData.subscriptionType,
    billingFrequency: displayData.subscriptionType === 'membership'
      ? displayData.billingFrequency
      : undefined,
    validityDays: displayData.subscriptionType === 'pass'
      ? displayData.validityDays
      : undefined,
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-12">
      {/* Success Message */}
      <div className="mb-8">
        <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900 sm:text-4xl" data-testid="subscription-confirmation-heading">
          {isGift ? 'Your gift is on its way' : 'Your subscription is confirmed'}
          <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-green-500 text-white">
            <CheckmarkIcon className="w-5 h-5" />
          </span>
        </h1>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* LEFT: Subscription Plan Card */}
        <SubscriptionPlanCard plan={subscriptionPlan} />

        {/* RIGHT: Confirmation Details */}
        <div className="flex flex-col gap-3">
          {/* Ticket Card */}
          <TicketCard borderColor="var(--theme-100)">
            {/* Top Section: Subscription Details */}
            <div className="px-6 pt-4">
              {displayData.subscriptionType === "gift" && displayData.recipients.length === 0 ? (
                // Rebuilt from the invoice on a refresh: the names were never
                // stored anywhere this page can read. Say so plainly rather
                // than render an empty space where recipients used to be.
                <InfoRow
                  label="Gifted to"
                  value="Sent — check your email for the details"
                  testId="subscription-confirmation-recipients-unavailable"
                />
              ) : displayData.subscriptionType === "gift" ? (
                // Who it went to, as the buyer typed them. Never resolved
                // against the accounts that resulted — those are the
                // recipients', not the buyer's to read.
                displayData.recipients.map((recipient, index) => (
                  <InfoRow
                    key={recipient.email}
                    label={
                      displayData.recipients.length === 1
                        ? "Gifted to"
                        : `Gifted to (${index + 1} of ${displayData.recipients.length})`
                    }
                    value={`${recipient.firstName} ${recipient.lastName} · ${recipient.email}`}
                    testId={`subscription-confirmation-recipient-${index}`}
                  />
                ))
              ) : (
                <InfoRow label="Start Date" value={startDateFormatted ?? ''} />
              )}
              {!isGift && (
                <InfoRow
                  label={
                    displayData.subscriptionType === "membership"
                      ? "Billing Cycle"
                      : "Expiration"
                  }
                  value={billingInfo ?? ''}
                />
              )}
              {displayData.subscriptionType === "membership" && displayData.nextBillingDate && (
                <InfoRow
                  label="Next Billing Date"
                  value={formatClubDate(
                    asUtcDate(new Date(displayData.nextBillingDate)),
                    club.timezone,
                    "MMM d, yyyy"
                  )}
                />
              )}
            </div>

            {/* Divider */}
            <TicketDivider punch />

            {/* Bottom Section: Purchase Details. No Purchase ID row — internal
                identifiers are noise to a member. */}
            <div className="px-6 pb-3">
              {/* Show setup fee if it exists (memberships only) */}
              {/* `!= null && > 0`, never `fee && …`: a $0 fee is falsy-but-RENDERABLE, and
                  React printed a literal "0" on every pass receipt. */}
              {stateData?.pricing?.setupFee != null && stateData.pricing.setupFee > 0 && (
                <InfoRow
                  label="Setup Fee"
                  value={`$${stateData.pricing.setupFee.toFixed(2)}`}
                />
              )}
              {/* Promo discount (7a) — nav-state only, like every pricing row:
                  gone on hard refresh by design (no client recompute — Group U). */}
              {stateData?.pricing?.discountTotal != null &&
                stateData.pricing.discountTotal > 0 && (
                  <InfoRow
                    label={stateData.pricing.promoLabel ?? "Discount"}
                    value={`-$${stateData.pricing.discountTotal.toFixed(2)}`}
                    testId="subscription-confirmation-discount-row"
                  />
                )}
              {/* Processing Fee — itemized from the canonical InvoiceDTO's reconciled
                  totals (fetched via useInvoiceQuery). Only rendered when > 0. */}
              {fetchedInvoice?.totals?.processingFee != null &&
                fetchedInvoice.totals.processingFee > 0 && (
                  <InfoRow
                    label={`Processing Fee (${formatFeeRate(fetchedInvoice.totals.processingFeeRate)})`}
                    value={`$${fetchedInvoice.totals.processingFee.toFixed(2)}`}
                  />
                )}
              <InfoRow
                label="Total Paid"
                value={totalPaid !== null ? `$${totalPaid.toFixed(2)}` : "Unable to load purchase price"}
                testId="subscription-confirmation-total-paid-row"
              />

              {/* Download Receipt Button - Omitted for Phase 1 */}
              {/* TODO Phase 2: Add receipt download when backend endpoint is ready */}
            </div>
          </TicketCard>

          {/* Action Buttons - Outside Ticket Card */}
          {/* Next step for a demo-required plan the member hasn't demoed for
              (demo bookings D40): the primary action is booking the demo, since
              standard booking is gated until it completes. A gift receipt opts
              out of the WHOLE condition, not just its own flag — the fallback
              reads the buyer's ACTIVE plan (the slim membership wire carries no
              template flags), and a buyer who is themselves gated must not see
              a demo CTA on someone else's gift. */}
          {displayData.subscriptionType !== 'gift'
            && (displayData.demoRequired || activePlanDemoRequired)
            && !demoCompleted && (
            <div className="mb-3 flex flex-col gap-3 sm:flex-row">
              <TenantLink
                to="/demo"
                className="flex-1 rounded-full bg-theme-600 px-6 py-3 font-medium text-white transition-colors hover:bg-theme-700 focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2 text-center"
                aria-label="Book your demo"
                data-testid="subscription-confirmation-demo-btn"
              >
                Book your demo to start booking
              </TenantLink>
            </div>
          )}
          <div className="flex flex-col gap-3 sm:flex-row">
            {/* <TenantLink
              to="/subscriptions/manage"
              className="flex-1 rounded-full border border-gray-300 bg-white px-6 py-3 font-medium text-fg-primary transition-colors hover:bg-bg-secondary focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2 text-center"
              aria-label="View my subscriptions"
            >
              View My Subscriptions
            </TenantLink> */}
            <TenantLink
              to="/"
              className="flex-1 rounded-full bg-theme-600 px-6 py-3 font-medium text-white transition-colors hover:bg-theme-700 focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2 text-center"
              aria-label="Go to main page"
              data-testid="subscription-confirmation-home-btn"
            >
              Go to Main Page
            </TenantLink>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Info Row Component
 *
 * Internal component for displaying labeled information with optional copy functionality.
 */
interface InfoRowProps {
  label: string;
  value: string;
  copyable?: boolean;
  testId?: string;
  onCopySuccess?: () => void;
  onCopyError?: () => void;
}

function InfoRow({ label, value, copyable = false, testId, onCopySuccess, onCopyError }: InfoRowProps) {
  return (
    <div className="flex items-center justify-between py-2" data-testid={testId}>
      <span className="text-sm font-medium text-fg-quaternary">{label}</span>
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-fg-primary">{value}</span>
        {copyable && (
          <CopyButton
            text={value}
            size="default"
            onCopySuccess={onCopySuccess}
            onCopyError={onCopyError}
          />
        )}
      </div>
    </div>
  );
}
